import isNil from "lodash/isNil";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getCurrentVenueCache } from "./use-get-current-venue";
import { useVenueId } from "~/store";
import { gqlReq } from "~/libs/gql";
import {
	GetCurrentVenueStaffsDocument,
	GetCurrentVenueStaffsQuery,
} from "~/types/__generated/gql/graphql";
import { queryClient } from "~/providers/query-provider";

void (
	/* GraphQL */ `
		query GetCurrentVenueStaffs {
			getCurrentVenueStaffs {
				_id
				type
				username
				first_name
				last_name
				position
				permissions
				venue_issued_id
			}
		}
	`
);

export const getCurrentVenueStaffsQueryKey = () => ["getCurrentVenueStaffs"];

export const getCurrentVenueStaffsOfflineQueryKey = () => [
	"getCurrentVenueStaffs",
	"offline",
];

const sortRolesByPosition = (
	roles: GqlResultOf<GetCurrentVenueStaffsQuery>,
): GqlResultOf<GetCurrentVenueStaffsQuery> => {
	const rolesWithPosition = roles
		.filter((role) => !isNil(role.position))
		.sort((a, b) => a.position! - b.position!);
	const rolesWithoutPosition = roles.filter((role) => isNil(role.position));

	return [...rolesWithPosition, ...rolesWithoutPosition];
};

const getCurrentVenueStaffsQueryFn = (signal?: AbortSignal) =>
	gqlReq({
		document: GetCurrentVenueStaffsDocument,
		signal,
	});

export const useGetCurrentVenueStaffs = () => {
	const queryClient = useQueryClient();

	const venueId = useVenueId();

	return useQuery({
		enabled: !!venueId,
		queryKey: getCurrentVenueStaffsQueryKey(),
		queryFn: ({ signal }) => getCurrentVenueStaffsQueryFn(signal),
		select: (data) => sortRolesByPosition(data),
		onSuccess: (data) => {
			queryClient.setQueryData(
				getCurrentVenueStaffsOfflineQueryKey(),
				sortRolesByPosition(data),
			);
		},
	});
};

export const useGetCurrentVenueStaffsOffline = () => {
	const queryClient = useQueryClient();

	return useQuery({
		queryKey: getCurrentVenueStaffsOfflineQueryKey(),
		queryFn: () =>
			sortRolesByPosition(
				queryClient.getQueryData<GqlResultOf<GetCurrentVenueStaffsQuery>>(
					getCurrentVenueStaffsOfflineQueryKey(),
					{ exact: true },
				) ?? [],
			),
	});
};

/** Offline-enabled venues refetch into both keys because offline login reads staff permissions. */
export const refreshCurrentVenueStaffs = async () => {
	if (!getCurrentVenueCache()?.is_offline_mode_enabled) {
		await queryClient.invalidateQueries({
			queryKey: getCurrentVenueStaffsQueryKey(),
			exact: true,
		});
		return;
	}
	await getCurrentVenueStaffsQueryFn()
		.then((data) => {
			queryClient.setQueryData(getCurrentVenueStaffsQueryKey(), data);
			queryClient.setQueryData(
				getCurrentVenueStaffsOfflineQueryKey(),
				sortRolesByPosition(data),
			);
		})
		.catch(console.error);
};
